"use client";

import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import { AnimatePresence, motion } from "framer-motion";
import { AnimateChangeInHeight } from "@/components/animations/animateHeight";
import { CheckIcon, EnvelopeIcon } from "@heroicons/react/20/solid";
import { PhoneValidation } from "@/components/phoneValidation";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { getCurrentTime, useSearchEngineDetector } from "@/lib/utils";
import Bowser from "bowser";
import ToggleSwitch from "@/components/ui/toggleSwitch";
import FinanceSwitch from "@/components/ui/financeToggle";
import { secureInput } from "@/lib/security";
import { australian_states, emailTypos } from "@/lib/constants";
import { DropDownField } from "@/components/ui/dropDownField";

async function sendEmail<T>(data: T) {
	try {
		const siteUrl = window.location.origin;
		const response = await axios.post(`${siteUrl}/wp-json/quick-form/v1/send-enquiry`, data);
		return response;
	} catch (error) {
		console.error("There was an error sending the email:", error);
	}
}

function usePrevious(value: number) {
	let ref = useRef(value);

	useEffect(() => {
		ref.current = value;
	}, [value]);

	return ref.current;
}

const variants = {
	initial: (isForwards: boolean) => {
		return {
			opacity: 0,
		};
	},
	animate: {
		opacity: 1,
	},
	exit: (isForwards: boolean) => {
		return {
			opacity: 0,
		};
	},
};

type QuickFormProps = {
	className?: string;
	siteTitle?: string;
	rootId?: HTMLElement;
};

export function GeneralForm({ className, siteTitle, rootId }: QuickFormProps) {
	// Get values and setters from Zustand store
	const { formValues, setFormValue, setFormError, resetFormValues } = useFormDataStore();
	const {
		setValidateFormDetails,
		toggleTC,
		setToggleTC,
		isPhoneVerified,
		setPhoneVerified,
		appSettings,
		setAppSettings,
		isSubmitted,
		setIsSubmitted,
		selectedIndex,
		setSelectedIndex,
		setFormErrors,
		hasAttemptedSubmit,
		setHasAttemptedSubmit,
		toggleFinance, //if customer needs finance yes/no
		setToggleFinance,
	} = useFormStateStore();

	const detectedSearchEngine = useSearchEngineDetector();
	const [searchEngine, setSearchEngine] = useState<string>("Direct");
	const [userIP, setUserIP] = useState("");
	const [usingVPN, setUsingVPN] = useState(false);
	const [browserInfo, setBrowserInfo] = useState({});
	const prevIndex = usePrevious(selectedIndex);
	const isForwards = prevIndex === undefined ? true : selectedIndex > prevIndex;
	const [stepsMaxIndex, setStepsMaxIndex] = useState(1);
	const sourceUrl = window.location.href;
	const { firstName, lastName, email, phone, budget, state, enquiry } = formValues;
	const [selectedState, setSelectedState] = useState(state);

	const stateOptions = Object.entries(australian_states).map(([value, option]) => ({
		value,
		option,
	}));

	useEffect(() => {
		if (state) {
			setSelectedState(state);
		}
	}, [state]);

	// Toggle Switch for T&C repeat for finance toggle
	const [enabledTC, setEnabledTC] = useState(toggleTC);
	const [enabledFN, setEnabledFN] = useState(toggleFinance);

	useEffect(() => {
		setToggleTC(enabledTC);
		if (errors.tc) {
			setErrors((prev) => ({ ...prev, tc: !enabledTC }));
		}
	}, [enabledTC]);

	useEffect(() => {
		setToggleFinance(enabledFN);
	}, [enabledFN]);

	const [emailMessage, setEmailMessage] = useState("");
	const formatBudget = (value: string) => {
		if (!value) return "";

		return new Intl.NumberFormat("en-AU", {
			style: "currency",
			currency: "AUD",
			maximumFractionDigits: 0,
		}).format(Number(value));
	};

	// Local state for form validation errors add budget, finance toggle and the users state
	const [errors, setErrors] = useState({
		firstName: false,
		lastName: false,
		phone: false,
		email: false,
		enquiry: false,
		tc: false,
		budget: false,
		state: false,
	});

	// Email Typo Validation Helper
	const getEmailValidationError = (emailValue: string): string => {
		const trimmed = emailValue.trim();
		if (trimmed === "") return "Email is required";

		const sanitizedValue = secureInput.email(trimmed);
		if (!sanitizedValue.isValid) return "Email is not correctly formatted";

		const fullValue = trimmed.toLowerCase();
		const parts = fullValue.split("@");

		if (parts.length === 2) {
			const [username, domain] = parts;
			const typoRegex = /\.(con\.au|cm\.au|con|cm|co)$/i;
			const endTypoRegex = /\.com[a-z]$/;

			if (emailTypos[domain]) {
				return `Did you mean ${username}@${emailTypos[domain]}?`;
			} else if (typoRegex.test(domain)) {
				const fixedDomain = domain.replace(typoRegex, (match) => {
					return match.includes(".au") ? ".com.au" : ".com";
				});
				return `Did you mean ${username}@${fixedDomain}?`;
			} else if (endTypoRegex.test(domain)) {
				const fixedDomain = domain.replace(endTypoRegex, (match) => {
					return match.includes(".au") ? ".com.au" : ".com";
				});
				return `Did you mean ${username}@${fixedDomain}?`;
			}
		}
		return "";
	};

	// Handle input changes with validation
	const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const sanitizedValue = secureInput.name(value);
		setFormValue("firstName", sanitizedValue.sanitized);
		setErrors((prev) => ({ ...prev, firstName: !sanitizedValue.isValid }));
	};

	const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const sanitizedValue = secureInput.name(value);
		setFormValue("lastName", sanitizedValue.sanitized);
		setErrors((prev) => ({ ...prev, lastName: !sanitizedValue.isValid }));
	};

	const handleEmailChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			const sanitizedValue = secureInput.email(value);
			setFormValue("email", sanitizedValue.sanitized);

			const emailErrorMsg = getEmailValidationError(sanitizedValue.sanitized);
			setErrors((prev) => ({ ...prev, email: emailErrorMsg !== "" }));
			setEmailMessage(emailErrorMsg);
		},
		[setFormValue],
	);

	const handleEnquiryChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = e.target.value;
		const sanitizedValue = secureInput.textarea(value);
		setFormValue("enquiry", sanitizedValue.sanitized);
		setErrors((prev) => ({ ...prev, enquiry: !sanitizedValue.isValid }));
	};

	const handleBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value.replace(/[^0-9]/g, "");
		const sanitizedValue = secureInput.numeric(value);
		setFormValue("budget", sanitizedValue.sanitized);
		setErrors((prev) => ({ ...prev, budget: !sanitizedValue.isValid }));
	};

	const handleFinanceToggle = (value: boolean) => {
		setEnabledFN(value);
		setToggleFinance(value);
		setFormValue("requireFinance", value ? "Yes" : "No");
	};

	// Function to validate all fields
	const validateForm = () => {
		const emailErrorMsg = getEmailValidationError(email);

		// add validation for finance toggle, budget and state
		const newErrors = {
			firstName: !secureInput.name(firstName).isValid,
			lastName: !secureInput.name(lastName).isValid,
			email: emailErrorMsg !== "",
			phone: !secureInput.phone(phone).isValid,
			enquiry: !secureInput.textarea(enquiry).isValid,
			tc: !enabledTC,
			budget: !secureInput.numeric(budget).isValid,
			state: state === "",
		};

		if (phone.trim() === "") {
			setFormError("phone", "Phone number is required");
		}

		setErrors(newErrors);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormErrors(hasError);
		setEmailMessage(emailErrorMsg);

		return !hasError; // Returns true if validation passes
	};

	// Store the validation function in Zustand so QuickForm can call it	add finance, budget and state validation
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [firstName, lastName, email, phone, enquiry, enabledTC, budget, state]);

	// Apply Color Theme Settings
	useEffect(() => {
		setAppSettings(siteTitle ?? "Car Consultants");
		if (!rootId) return;

		rootId?.style.setProperty(`--cC-primary`, appSettings.themeColor);
		rootId?.style.setProperty(`--cC-secondary`, appSettings.themeHeaderTxt);
		rootId?.style.setProperty(`--cC-primary-darker`, appSettings.hoverColor);
		rootId?.style.setProperty(`--cC-bullet-bg`, appSettings.bulletBG);
	}, [appSettings]);

	const scrollToHeading = () => {
		if (!rootId) return;

		if (window.matchMedia("(max-width: 767px)").matches) {
			setTimeout(() => {
				const additionalOffset = 0; // adjust if you want some spacing above the form
				const targetPosition =
					rootId.getBoundingClientRect().top + window.scrollY - additionalOffset;

				window.scrollTo({
					top: targetPosition,
					behavior: "smooth",
				});
			}, 100);
		}
	};

	useEffect(() => {
		setSearchEngine(detectedSearchEngine);
	}, [detectedSearchEngine]);

	useEffect(() => {
		const browser = Bowser.parse(window.navigator.userAgent);
		const info = `${browser.browser.name} ${browser.browser.version}, ${browser.os.name}, ${browser.platform.type}`;
		setBrowserInfo(info);
	}, []);

	window.leadGenerated = window.leadGenerated || false;

	const loadDataLayerScript = () => {
		if (!window.leadGenerated) {
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: "generate_lead",
				enhanced_conversion_data: {
					email: email,
					phone_number: "+61" + phone?.substring(1),
					first_name: firstName,
					last_name: lastName,
				},
			});
			window.leadGenerated = true;
		}
	};

	const getUserIP = useCallback(async () => {
		const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/getIP`);
		const data = await response.data;
		setUserIP(data.ip);
		setUsingVPN(data.vpn);
	}, []);

	const sendMail = useMutation({
		mutationKey: [formValues],
		mutationFn: async () => {
			const zapPost = {
				...formValues,
				dateSubmitted: getCurrentTime(),
				siteTitle: `${siteTitle} Vehicle Enquiry Form`,
				formName: `${siteTitle} Vehicle Enquiry Form Submission`,
				source: sourceUrl,
				source_id: 18,
				userIP: userIP,
				isUsingVPN: usingVPN.toString(),
				userAgent: browserInfo,
				searchEngine: searchEngine,
			};
			const data = await sendEmail(zapPost);
			return data;
		},
		onSuccess() {
			resetFormValues();
			setEnabledTC(false);
		},
	});

	// Prevents Next button from being spammed causing skipping multiple pages.
	const [isPageLoading, setIsPageLoading] = useState(false);

	useEffect(() => {
		if (hasAttemptedSubmit && !isPhoneVerified && phone.trim() !== "") {
			setFormError("phone", "Please verify your phone number.");
		}
	}, [hasAttemptedSubmit, isPhoneVerified]);

	function submit() {
		setHasAttemptedSubmit(true);
		if (isPageLoading) return;

		scrollToHeading();

		const isValid = validateForm();
		console.log("isValid:", isValid, "errors:", errors, "formValues:", formValues);

		if (hasAttemptedSubmit && !isPhoneVerified && phone.trim() !== "") {
			setFormError("phone", "Please verify your phone number.");
		}

		if (!isValid) return;

		if (isPhoneVerified && enquiry !== "") {
			sendMail.mutate();
			setToggleTC(false);
			setIsSubmitted(true);
			loadDataLayerScript();
			setFormError("phone", "");
			setSelectedIndex(stepsMaxIndex);
		}
	}

	function close() {
		scrollToHeading();
		setSelectedIndex(0);
		setToggleTC(false);
		setPhoneVerified(false);
		if (isSubmitted) {
			setIsSubmitted(false);
			setHasAttemptedSubmit(false);
		}
	}

	useEffect(() => {
		if (isPhoneVerified) {
			getUserIP();
		}
	}, [isPhoneVerified]);

	const handleKeyDown = useCallback((event: any) => {
		if (event.key === "Enter") {
			event.preventDefault();
		}
	}, []);

	// Reset form errors on focus
	const handleFocus = (field: keyof typeof errors) => {
		setErrors((prev) => ({ ...prev, [field]: false }));
	};

	return (
		<div className={clsx(className, "")}>
			{/* Form */}
			<AnimateChangeInHeight>
				<form
					className="cC-relative cC-px-2 cC-pt-6 cC-pb-2 sm:cC-px-2"
					onKeyDown={handleKeyDown}
					onSubmit={(e) => e.preventDefault()}
				>
					{/* Loading Overlay */}
					{sendMail.isPending && (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
							className="cC-absolute cC-w-full cC-h-full cC-top-0 cC-left-0 cC-bg-slate-200 cC-bg-opacity-75 cC-z-50"
						>
							<div className="cC-flex cC-justify-center cC-items-center cC-h-full cC-w-full">
								<div role="status">
									<svg
										aria-hidden="true"
										className="cC-w-12 cC-h-12 cC-text-opacity-0 cC-text-white cC-animate-spin cC-bg-opacity-0 cC-fill-[--cC-theme-color]"
										viewBox="0 0 100 101"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
											fill="currentColor"
										/>
										<path
											d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
											fill="currentFill"
										/>
									</svg>
									<span className="cC-sr-only">Loading...</span>
								</div>
							</div>
						</motion.div>
					)}

					<AnimatePresence initial={false} mode="wait" custom={isForwards}>
						{selectedIndex === 0 && (
							<motion.div
								key={0}
								initial="initial"
								animate="animate"
								exit="exit"
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								variants={variants}
								custom={isForwards}
								className="cC-space-y-4"
							>
								<FormHeading />
								<div className="cC-flex cC-flex-col cC-gap-2">
									<div className="cC-flex-1">
										<div className="cC-relative cC-mt-2">
											<input
												name="firstName"
												value={firstName}
												onChange={handleFirstNameChange}
												aria-label="First Name"
												type="text"
												id="firstName"
												placeholder="First Name"
												maxLength={30}
												autoComplete="given-name"
												onFocus={() => handleFocus("firstName")}
												className={clsx(
													errors.firstName
														? "cC-ring-red-500 focus:cC-ring-red-500"
														: "cC-ring-gray-300 focus:cC-ring-gray-900",
													"cC-block cC-w-full cC-rounded-md cC-shadow-sm !cC-border-0 !cC-py-2.5 cC-text-gray-900 cC-ring-1 cC-outline-1 cC-outline-[--cC-primary] cC-ring-inset focus:cC-ring-1 focus:cC-ring-inset !cC-pl-3 placeholder:cC-text-gray-400 sm:cC-text-md sm:cC-leading-6",
												)}
											/>
										</div>
										{errors.firstName && (
											<span className="cC-text-xs cC-text-red-500">
												First Name Required
											</span>
										)}
									</div>
									<div className="cC-flex-1">
										<div className="cC-relative cC-mt-2">
											<input
												name="lastName"
												value={lastName}
												onChange={handleLastNameChange}
												aria-label="Last Name"
												type="text"
												id="lastName"
												placeholder="Last Name"
												maxLength={30}
												autoComplete="family-name"
												onFocus={() => handleFocus("lastName")}
												className={clsx(
													errors.lastName
														? "cC-ring-red-500 focus:cC-ring-red-500"
														: "cC-ring-gray-300 focus:cC-ring-gray-900",
													"cC-block cC-w-full cC-rounded-md cC-shadow-sm !cC-border-0 !cC-py-2.5 cC-text-gray-900 cC-ring-1 cC-outline-1 cC-outline-[--cC-primary] cC-ring-inset focus:cC-ring-1 focus:cC-ring-inset !cC-pl-3 placeholder:cC-text-gray-400 sm:cC-text-md sm:cC-leading-6",
												)}
											/>
										</div>
										{errors.lastName && (
											<span className="cC-text-xs cC-text-red-500">
												Last Name Required
											</span>
										)}
									</div>
								</div>
								<div className="cC-grid cC-grid-cols-1 cC-gap-2">
									<div className="cC-relative cC-flex-1">
										<div className="cC-relative cC-flex">
											<div className="cC-pointer-events-none cC-absolute cC-inset-y-0 cC-left-0 cC-flex cC-items-center cC-pl-3">
												<EnvelopeIcon
													className="cC-h-5 cC-w-5 cC-text-gray-400"
													aria-hidden="true"
												/>
											</div>
											<input
												value={email}
												onChange={handleEmailChange}
												aria-label="Email Address"
												type="email"
												id="email"
												placeholder="Email Address"
												maxLength={50}
												autoComplete="email"
												onFocus={() => handleFocus("email")}
												className={clsx(
													errors.email
														? "cC-ring-red-500 focus:cC-ring-red-500"
														: "cC-ring-gray-300 focus:cC-ring-gray-900",
													"cC-block cC-w-full cC-rounded-md cC-shadow-sm !cC-border-0 !cC-py-2.5 !cC-pl-10 cC-text-gray-900 cC-ring-1 cC-outline-1 cC-outline-[--cC-primary] cC-ring-inset focus:cC-ring-1 focus:cC-ring-inset placeholder:cC-text-gray-400 sm:cC-text-md sm:cC-leading-6 cC-bg-none",
												)}
											/>
										</div>
										{errors.email && emailMessage && (
											<span className="cC-text-xs cC-text-red-500">
												{emailMessage}
											</span>
										)}
									</div>

									<div className="cC-bg-white cC-rounded-md cC-w-full cC-mt-2 cC-gap-2">
										<DropDownField
											options={stateOptions}
											value={selectedState}
											placeholder="Select your state"
											onChange={(e) => {
												//dropdown component expects onChange
												const value = e.target.value;
												setSelectedState(value);
												setFormValue("state", value);
											}}
											className={clsx(
												errors.state
													? "cC-ring-red-500 focus:cC-ring-red-500"
													: "cC-ring-gray-300 focus:cC-ring-gray-900",
												"cC-block cC-w-full cC-shadow-sm cC-rounded-md !cC-border-0 !cC-px-3.5 !cC-py-2.5 cC-ring-1 cC-outline-1 cC-outline-[--cC-primary] cC-ring-inset focus:cC-ring-1 focus:cC-ring-inset sm:cC-text-md sm:cC-leading-6",
											)}
										/>
										{errors.state && (
											<span className="cC-text-xs cC-text-red-500">
												This field is required
											</span>
										)}
									</div>
								</div>

								<div className="">
									{!isPhoneVerified ? (
										<>
											<PhoneValidation />
										</>
									) : (
										<>
											<div className="cC-flex-1 cC-flex cC-items-center cC-border cC-border-green-500 cC-bg-green-50 cC-p-2 cC-rounded-md">
												<input
													defaultValue={phone}
													className="cC-bg-transparent !cC-outline-none !cC-border-none cC-flex-1"
													readOnly
												/>
												<CheckIcon className="cC-w-4 cC-h-4 max-md:cC-w-5 max-md:cC-h-5 cC-text-green-500 cC-ml-2" />
											</div>
										</>
									)}
								</div>

								<div className="cC-flex cC-flex-col cC-gap-2">
									<div className="cC-bg-white cC-rounded-md">
										<div>
											<input
												value={formatBudget(budget)}
												onChange={handleBudgetChange}
												aria-label="Budget"
												type="text"
												id="budget"
												maxLength={8}
												placeholder="Budget"
												className={clsx(
													errors.budget
														? "cC-ring-red-500 focus:cC-ring-red-500"
														: "cC-ring-gray-300 focus:cC-ring-gray-900",
													"cC-block cC-w-full cC-rounded-md cC-shadow-sm !cC-border-0 !cC-px-3.5 !cC-py-2 cC-text-gray-900 cC-ring-1 cC-outline-1 cC-outline-[--cC-primary] cC-ring-inset focus:cC-ring-1 focus:cC-ring-inset placeholder:cC-text-gray-400 sm:cC-text-md sm:cC-leading-6 cC-bg-none !important",
												)}
											/>
											{errors.budget && (
												<span className="cC-text-xs cC-text-red-500">
													This field is required
												</span>
											)}
										</div>
									</div>
									<div className="cC-mt-2">
										<FinanceSwitch
											label=""
											enabled={enabledFN}
											setEnabled={(nextValue) => {
												const isEnabled = Boolean(nextValue);
												handleFinanceToggle(isEnabled);
											}}
										/>
									</div>
								</div>

								<div className="cC-space-y-4 sm:cC-flex sm:cC-gap-3 sm:cC-space-y-0 cC-mt-2">
									<div className="cC-flex-1">
										<textarea
											value={enquiry}
											onChange={handleEnquiryChange}
											name="enquiry"
											id="enquiry"
											placeholder="Please state your desired make, model and year."
											rows={4}
											maxLength={150}
											className={clsx(
												errors.enquiry
													? "cC-ring-red-500 focus:cC-ring-red-500"
													: "cC-ring-gray-300 focus:cC-ring-gray-900",
												"cC-block cC-w-full cC-rounded-md cC-shadow-sm !cC-border-0 !cC-px-3.5 !cC-py-2 cC-text-gray-900 cC-ring-1 cC-outline-1 cC-outline-[--cC-primary] cC-ring-inset focus:cC-ring-1 focus:cC-ring-inset placeholder:cC-text-gray-400 sm:cC-text-md sm:cC-leading-6 cC-bg-none",
											)}
										/>
										{errors.enquiry && (
											<span className="cC-text-xs cC-text-red-500">
												This field is required
											</span>
										)}
									</div>
								</div>

								{/* Terms and Conditions */}
								<div id="tc-toggle" className={clsx("!cC-mt-4")} tabIndex={-1}>
									<ToggleSwitch
										label=""
										enabled={enabledTC}
										setEnabled={setEnabledTC}
									/>
									{errors.tc && (
										<span className="cC-block cC-mt-1 cC-text-xs cC-text-red-500">
											Please agree to the terms and conditions to continue
										</span>
									)}
								</div>
							</motion.div>
						)}

						{/* Success Page */}
						{selectedIndex === stepsMaxIndex && (
							<motion.div
								key={stepsMaxIndex}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
								className="space-y-4"
							>
								<FormSuccessHeading />
							</motion.div>
						)}
					</AnimatePresence>
				</form>
			</AnimateChangeInHeight>

			{/* Navigation */}
			{selectedIndex < stepsMaxIndex ? (
				<div className="cC-flex cC-items-center cC-flex-wrap cC-justify-end cC-border-t cC-border-gray-200 cC-bg-white cC-mt-2 cC-px-4 cC-py-3 sm:cC-px-6 max-sm:cC-justify-center sm:cC-flex-nowrap">
					<div className="cC-flex cC-flex-1 cC-justify-end cC-mt-2 cC-flex-nowrap max-sm:cC-mt-0 max-sm:cC-justify-center md:cC-flex-nowrap max-sm:cC-flex-wrap">
						<button
							className={clsx(
								!toggleTC
									? "cC-text-gray-500 cC-bg-gray-100 hover:cC-cursor-not-allowed cC-ring-gray-100 cC-basis-auto cC-w-auto"
									: "cC-bg-[--cC-primary] hover:cC-bg-[--cC-primary-darker] cC-text-white",
								"cC-basis-2/4 cC-justify-center cC-w-auto cC-relative cC-ml-3 cC-inline-flex cC-items-center cC-rounded-md cC-px-3 cC-py-2 cC-text-md cC-font-semibold md:cC-basis-auto sm:cC-w-auto sm:cC-basis-full",
							)}
							onClick={(e) => {
								e.preventDefault();
								submit();
							}}
						>
							Submit
						</button>
					</div>
				</div>
			) : (
				<>
					<div className="cC-flex cC-items-center cC-flex-wrap cC-justify-center cC-border-t cC-border-gray-200 cC-bg-white cC-mt-2 cC-px-4 cC-py-3 max-sm:cC-px-6">
						<button
							className="cC-bg-[--cC-primary] hover:cC-bg-[--cC-primary-darker] cC-relative cC-mx-auto cC-block cC-text-center cC-w-1/3 cC-rounded-md cC-px-3 cC-py-2 cC-text-md cC-font-semibold cC-text-white"
							onClick={() => {
								close();
							}}
						>
							Close
						</button>
					</div>
				</>
			)}
		</div>
	);
}

function FormHeading({ className }: { className?: string }) {
	return (
		<>
			<div className={clsx(className, "")}>
				<div className="cC-mt-3 cC-text-center sm:cC-mt-5">
					<h2 className="cC-text-[2.2rem] cC-mb-3 cC-font-semibold cC-leading-8 cC-text-[--cC-secondary]">
						Vehicle Enquiry Form
					</h2>
				</div>
			</div>
		</>
	);
}

function FormSuccessHeading({ className }: { className?: string }) {
	return (
		<>
			<div className={clsx(className, "")}>
				<div className="cC-mx-auto cC-flex cC-h-20 cC-w-20 cC-items-center cC-justify-center cC-rounded-full cC-bg-[--cC-bullet-bg]">
					<CheckIcon
						className="cC-h-10 cC-w-10 cC-text-[--cC-primary]"
						aria-hidden="true"
					/>
				</div>
				<div className="cC-mt-3 cC-text-center sm:cC-mt-5">
					<h3 className="cC-text-[2rem] cC-mb-3 cC-font-semibold cC-leading-8 cC-text-[--cC-secondary]">
						Enquiry Received
					</h3>
					<div className="cC-mt-5 cC-mb-5">
						<p className="cC-text-md cC-text-gray-500">
							Thanks for contacting us! We will get in touch with you shortly.
						</p>
					</div>
				</div>
			</div>
		</>
	);
}
